<template>
  <div
    :class="[$options.name, classList, 'tw:w-12 tw:min-w-12 tw:h-full']"
    @mouseleave="!keepHovered ? (isHover = false) : null"
    @mouseover="isHover = true"
  >
    <div
      :class="[
        `${$options.name}__content`,
        isDev ? `${$options.name}__content--isDev` : '',
        'tw:w-full tw:h-full tw:flex tw:flex-col tw:border-r tw:border-default-200',
      ]"
    >
      <MainMenuHeader
        :is-hover="isHover"
        :is-pinned="isPinned"
        @keep-hovered="setKeepHovered"
        @toggle-menu-pin="toggleMenuPin()"
      />

      <hr
        v-if="showTopBorder"
        :class="['tw:my-0 tw:mx-4', isDev ? 'tw:!border-t-default-200' : 'tw:!border-t-branding-600']"
      />

      <div :class="[`${$options.name}__navigation`, 'tw:p-2 tw:flex-1']">
        <ul class="tw:m-0 tw:p-0">
          <MainMenuLink
            v-for="(item, index) in mainTree"
            :id="'navigation-item__' + (index === 0 ? 'first' : index)"
            :key="item.name + '-' + index"
            :active-menu="activeMenu"
            :is-last-item="false"
            :item="item"
            :menu-badges="menuBadges"
            :visible="isHover || isPinned"
            @update:active-menu="activeMenu = $event"
          />
          <MainMenuLink
            id="navigation-item__last"
            :is-last-item="true"
            :item="{
              name: 'ai-banner',
              label: trans._branding.aiBanner.header.title,
              path: '',
              disabled: false,
              iconName: 'sparkles',
              children: [],
            }"
            :visible="isHover || isPinned"
            @click="handleAiBannerClick"
          >
            <template v-if="hasNewContent" #badge>
              <span class="tw:w-2 tw:h-2 tw:bg-blue-500 tw:rounded-full tw:animate-pulse"></span>
            </template>
          </MainMenuLink>
        </ul>
      </div>

      <hr
        v-if="showBottomBorder"
        :class="['tw:my-0 tw:mx-4', isDev ? 'tw:!border-t-default-200' : 'tw:!border-t-branding-600']"
      />

      <MainMenuFooter :is-hover="isHover" :is-pinned="isPinned" @keep-hovered="setKeepHovered" />
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, Ref, ref, watch, onUnmounted } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { mainTree, settingsTree, disableItemsForSuspended } from "./MainMenu.helpers.ts";
import MainMenuLink from "./MainMenuLink.vue";
import MainMenuFooter from "./MainMenuFooter.vue";
import MainMenuHeader from "vue-app/components/MainMenu/MainMenuHeader.vue";
import { useIsScreenMobile } from "vue-app/composables/useIsScreenMobile/useIsScreenMobile.composable";
import useRestoreTracker from "vue-app/composables/useRestoreTracker";
import { DTOMenuIndicatorResponse } from "vue-app/api/dto";
import { getMenuIndicators } from "vue-app/api/menu/menu.ts";
import { amplitudeUpdateAutomationProperties } from "vue-app/utils/amplitude";
import { useAiBannerStore } from "vue-app/components/AiBanner/AiBanner.store.ts";
import { storeToRefs } from "pinia";

export default defineComponent({
  name: "MainMenu",
  components: {
    MainMenuHeader,
    MainMenuLink,
    MainMenuFooter,
  },
  props: {
    isPinned: Boolean,
  },
  emits: ["change", "open-ai-banner"],
  setup(props, context) {
    const store = useStore();
    const route = useRoute();
    const isDev = window.EDRONE_CONTEXT.user.isDev;
    const isDemo = window.EDRONE_CONTEXT.isDemo;
    const activeMenu = ref<string | null>(null);

    const aiBannerStore = useAiBannerStore();
    const { hasNewContent } = storeToRefs(aiBannerStore);

    const { isMobile } = useIsScreenMobile();

    const { getTrackerLeaveData, removeTrackerData, handleRestore, handleIntercom, isLoading } = useRestoreTracker();
    getTrackerLeaveData();

    const isSuperagent = computed(() => store.getters["global/isSuperagent"]);

    watch(
      () => route.meta.routeName,
      () => {
        setRouteTitle();
      }
    );

    const setRouteTitle = () => {
      if (window.EDRONE_CONTEXT.isLocal) {
        document.title = `edrone LOCAL - ${route.meta.routeName}`;
      } else {
        document.title = `edrone - ${route.meta.routeName}`;
      }
    };

    setRouteTitle();

    const isHover = ref(false);

    const classList = computed(() => {
      return {
        "MainMenu--hover": isHover.value,
        "MainMenu--pinned": props.isPinned,
      };
    });

    function toggleMenuPin() {
      store.dispatch("global/setMenuPinned", !props.isPinned);
      context.emit("change");
    }

    const keepHovered = ref(false);
    function setKeepHovered(value: boolean) {
      keepHovered.value = value;
      if (!value) {
        isHover.value = false;
      }
    }

    const showBottomBorder = ref(false);
    const showTopBorder = ref(false);
    const listItemFirst = ref<null | Element>(null);
    const listItemLast = ref<null | Element>(null);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === listItemLast.value) {
            showBottomBorder.value = !entry.isIntersecting;
          }
          if (entry.target === listItemFirst.value) {
            showTopBorder.value = !entry.isIntersecting;
          }
        });
      },
      {
        threshold: 1.0,
      }
    );

    const getMenuBadges = async () => {
      try {
        menuBadges.value = await getMenuIndicators();

        if (menuBadges.value) {
          await amplitudeUpdateAutomationProperties(menuBadges.value);
        }
      } catch (error) {
        console.error("Failed to fetch menu badges:", error);
      }
    };
    const menuBadges: Ref<DTOMenuIndicatorResponse | null> = ref(null);
    const badgesInterval = ref<ReturnType<typeof setInterval> | null>(null);
    let pubsubToken: string;

    const startBadgesInterval = () => {
      if (badgesInterval.value) {
        clearInterval(badgesInterval.value);
      }
      badgesInterval.value = setInterval(() => {
        getMenuBadges();
      }, 60000);
    };

    const stopBadgesInterval = () => {
      if (badgesInterval.value) {
        clearInterval(badgesInterval.value);
        badgesInterval.value = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopBadgesInterval();
      } else {
        getMenuBadges();
        startBadgesInterval();
      }
    };

    onMounted(() => {
      if (!isDev) {
        disableItemsForSuspended(mainTree.value);
        disableItemsForSuspended(settingsTree.value);
      }

      listItemFirst.value = document.querySelector("#navigation-item__first");
      listItemLast.value = document.querySelector("#navigation-item__last");

      if (listItemFirst.value && listItemLast.value) {
        observer.observe(listItemFirst.value);
        observer.observe(listItemLast.value);
      }

      getMenuBadges();
      startBadgesInterval();
      pubsubToken = window.pubSub.subscribe("menu.badges.refresh", () => {
        getMenuBadges();
      });
      document.addEventListener("visibilitychange", handleVisibilityChange);
    });

    onUnmounted(() => {
      stopBadgesInterval();
      window.pubSub.unsubscribe(pubsubToken);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    });

    const handleAiBannerClick = () => {
      hasNewContent.value = false;
      context.emit("open-ai-banner");
    };

    return {
      handleAiBannerClick,
      hasNewContent,
      isSuperagent,
      isLoading,
      handleIntercom,
      handleRestore,
      classList,
      isHover,
      toggleMenuPin,
      mainTree,
      setKeepHovered,
      keepHovered,
      isMobile,
      showBottomBorder,
      showTopBorder,
      isDemo,
      isDev,
      removeTrackerData,
      activeMenu,
      menuBadges,
    };
  },
});
</script>

<style scoped>
@reference "vue-app/assets/scss/main.css";

.MainMenu {
  transition: width 0.3s ease-out, min-width 0.3s ease-out;
}

.MainMenu__content {
  transition: width 0.3s ease-out;
  background: linear-gradient(354deg, #ffff9c 10.94%, #fffff5 43.75%);
}

.MainMenu__content--isDev {
  @apply tw:bg-gradient-to-t tw:from-default-000 tw:to-default-000;
}

/* scrollbar */
.MainMenu__navigation::-webkit-scrollbar {
  display: none;
}

.MainMenu__navigation {
  -ms-overflow-style: none;
  scrollbar-width: none;
  overflow-y: auto;
}

.MainMenu.MainMenu--hover .MainMenu__content {
  width: 15rem;
}

.MainMenu.MainMenu--pinned {
  min-width: 15rem;
  width: 15rem;
}

.MainMenu.MainMenu--pinned .content {
  width: 15rem;
}
</style>
